/** SUNTRONIX ONE — ONE VIBE.
 *
 *  A restrained visual-personality layer over the existing design system.
 *  70% neutral, 30% vibrant.  Each workspace (Space) gets a contextual accent
 *  that colours the active navigation indicator + Space icon only — inactive
 *  spaces stay calm, and semantic status colours are never changed for
 *  decoration.
 */

/* Workspace accent tokens — keyed by Space id (see constants/spaces.js). */
export const WORKSPACE_ACCENTS = {
  my_one:        { label: "My ONE",         hex: "#246BFD", soft: "#EFF5FF" }, // Electric Blue
  sales_desk:    { label: "Sales Desk",     hex: "#7657FF", soft: "#F1EEFF" }, // Violet
  customers:     { label: "Customers",      hex: "#2962FF", soft: "#EAF0FF" }, // Royal Blue
  technology:    { label: "Technology",     hex: "#0891B2", soft: "#E5F7FB" }, // Cyan
  operations:    { label: "Operations",     hex: "#2962FF", soft: "#EAF0FF" }, // Electric Blue
  support:       { label: "Support",        hex: "#4F46E5", soft: "#EEF0FF" }, // Indigo
  services:      { label: "Services",       hex: "#4F46E5", soft: "#EEF0FF" }, // Indigo
  commercial:    { label: "Commercial",     hex: "#B7791F", soft: "#FBF3E4" }, // Amber / Gold
  finance:       { label: "Finance",        hex: "#B7791F", soft: "#FBF3E4" }, // Gold
  insights:      { label: "Insights",       hex: "#4F46E5", soft: "#EEF0FF" }, // Indigo
  "legacy-insights": { label: "Analytics",  hex: "#4F46E5", soft: "#EEF0FF" },
  admin:         { label: "Administration", hex: "#475569", soft: "#F1F5F9" }, // Slate (calm)
  continuity:    { label: "Continuity",     hex: "#7C3AED", soft: "#F3EEFF" }, // Deep Purple
  relationships: { label: "Relationships",  hex: "#DB2777", soft: "#FDF0F7" }, // Warm Rose
};

const DEFAULT_ACCENT = { label: "SUNTRONIX", hex: "#246BFD", soft: "#EFF5FF" };

export function accentForSpace(spaceId) {
  return WORKSPACE_ACCENTS[spaceId] || DEFAULT_ACCENT;
}

/* Celebration palette — Electric Blue / Cyan / Violet / Emerald / Amber. */
export const MOMENT_COLORS = ["#246BFD", "#00C8FF", "#7657FF", "#18D5A4", "#FFB84D"];

/* Semantic status → pill classes.  Colour is always paired with a label. */
export const STATUS_PILL = {
  DRAFT:            "bg-slate-100 text-slate-600 border-slate-200",
  PENDING:          "bg-amber-50 text-amber-700 border-amber-200",
  REVIEW_REQUIRED:  "bg-amber-50 text-amber-700 border-amber-200",
  AWAITING_REVIEW:  "bg-amber-50 text-amber-700 border-amber-200",
  PROCESSING:       "bg-blue-50 text-blue-700 border-blue-200",
  IN_FULFILMENT:    "bg-blue-50 text-blue-700 border-blue-200",
  READY:            "bg-cyan-50 text-cyan-700 border-cyan-200",
  READY_TO_SHIP:    "bg-cyan-50 text-cyan-700 border-cyan-200",
  SHIPPED:          "bg-cyan-50 text-cyan-700 border-cyan-200",
  WON:              "bg-emerald-50 text-emerald-700 border-emerald-200",
  ACCEPTED:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  COMPLETED:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  DELIVERED:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  ACTIVE:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  SENT:             "bg-emerald-50 text-emerald-700 border-emerald-200",
  FAILED:           "bg-rose-50 text-rose-700 border-rose-200",
  OVERDUE:          "bg-rose-50 text-rose-700 border-rose-200",
  CANCELLED:        "bg-slate-100 text-slate-400 border-slate-200 line-through",
  RETURNED:         "bg-slate-100 text-slate-400 border-slate-200",
  EXACT_MATCH:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  NEW_RECORD:       "bg-blue-50 text-blue-700 border-blue-200",
  PROBABLE_MATCH:   "bg-amber-50 text-amber-700 border-amber-200",
  POSSIBLE_MATCH:   "bg-amber-50 text-amber-700 border-amber-200",
  CONFLICT:         "bg-rose-50 text-rose-700 border-rose-200",
  COMMITTED:        "bg-emerald-50 text-emerald-700 border-emerald-200",
  STAGED:           "bg-slate-100 text-slate-600 border-slate-200",
  ANALYZED:         "bg-blue-50 text-blue-700 border-blue-200",
  ROLLED_BACK:      "bg-slate-100 text-slate-500 border-slate-200",
  SUPERSEDED:       "bg-slate-100 text-slate-500 border-slate-200",
  SENT_TO_CUSTOMER: "bg-emerald-50 text-emerald-700 border-emerald-200",
  ISSUED:           "bg-blue-50 text-blue-700 border-blue-200",
  LOST:             "bg-rose-50 text-rose-700 border-rose-200",
  REJECTED:         "bg-rose-50 text-rose-700 border-rose-200",
};

export function pillClass(status) {
  const key = String(status || "").toUpperCase().replace(/\s+/g, "_");
  return STATUS_PILL[key] || "bg-slate-100 text-slate-600 border-slate-200";
}
